import * as React from 'react';
import { Filter, SelectedFilter, FilterOptions } from '..';
import {
	SingleTextFilterProvider,
	SingleIntegerFilterProvider,
	SingleDecimalFilterProvider,
	SingleBooleanFilterProvider,
	SingleDropdownFilterProvider,
} from './SingleValue';
import { MultipleValueFilterProvider, MultipleDropdownFilterProvider } from './MultipleValue';
import { RangedIntegerFilterProvider, RangedDecimalFilterProvider } from './RangedValue';
import { SingleAutoSuggestValueFilterProvider, MultipleAutoSuggestValueFilterProvider } from './AutoSuggestValue';
import { EntityDefinition } from 'types';
import { SavedFilter } from 'components/shared/list/saved-filters/SavedFilterPicker';
import { MultipleDateRangePresetProvider } from './MultipleRangePresetValue';
import { SingleDateFilterProvider } from './SingleDateValue';
import { SingleDateRangePresetFilterProvider } from './SingleDateRangePresetValue';
import { FilterType } from './Types';

/**
 * Required generic contract for filter edit components.
 *
 * @export
 * @interface FilterEditProps
 * @template T
 */
export interface FilterEditProps<T> {
	/**
	 * Returns the definition of the filter, which includes the type, whether it's nullable, the name, etc.
	 *
	 * @type {Filter}
	 * @memberof FilterEditProps
	 */
	filter: Filter;

	/**
	 * Returns the value for whatever type is needed. We attempt to strongly type everything as much as possible, but
	 * there are some limitations. The types should be available when working on edit components and providers which
	 * are the most important parts.
	 *
	 * @type {T}
	 * @memberof FilterEditProps
	 */
	value: T;

	/**
	 * Callback function to lift state from filter edit components up to Filters
	 *
	 * @memberof FilterEditProps
	 */
	onChange: (value: any, stopEditing: boolean) => void;
}

/**
 * Required, generic contract used by all filter providers
 *
 * @export
 * @interface FilterProviderBase
 * @template T
 */
export interface FilterProviderBase<T = any> {
	/**
	 * Returns the component used by your filter type. This is one of the places where TS falls short, and we just return
	 * any to make our lives easier. This is problematic because the return type can vary wildly, from stateless functional
	 * components, to components with state, to generic components potentially, etc.
	 *
	 * @returns {*}
	 * @memberof FilterProviderBase
	 */
	getComponent(): React.ComponentType;

	/**
	 * Based on the filter that is selected, this will return a set of additional props to be passed down to the actual
	 * component used for filtering. This is primarily used for dropdown values but it could be used for other things.
	 *
	 * @param {Filter} filter
	 * @returns {{}}
	 * @memberof FilterProviderBase
	 */
	getComponentProps(filter: Filter): any;

	/**
	 * Takes the value entered by the user in the filter edit component and converts it into something readable. This is
	 * primarily displayed in the list of selected filters after some are added. Note that this can
	 * return JSX if necessary, but plain strings will be valid too.
	 *
	 * @param {T} value
	 * @returns {React.ReactNode}
	 * @memberof FilterProviderBase
	 */
	getFriendlyValueForDisplay(value: T, filterOptions?: FilterOptions): React.ReactNode;

	/**
	 * Takes the value component of the query string and must return a strongly typed version of the value.
	 * For example, for a simple text filter, where the query is Code=SomeCode, `query` here would contain `'SomeCode'`
	 *
	 * @param {string} query
	 * @param {Filter} filter
	 * @returns {(T | null)}
	 * @memberof FilterProviderBase
	 */
	getValueFromQueryString(query: string, filter: Filter): T | null;

	/**
	 * Takes the strongly typed value selected by the user, and converts it into a QueryString value component.
	 * For example, if you have a text box with text `'SomeCode'`, it would just return `'SomeCode'`. The QueryString itself
	 * is built later on in the process, and will automatically add the code for you, so you just need to return the value
	 * component of the QueryString parameter.
	 *
	 * @param {T} value
	 * @param filter
	 * @param forClient
	 * @returns {string}
	 * @memberof FilterProviderBase
	 */
	getQueryStringFromValue(value: T, filter: Filter, target: QueryStringTarget): string;

	/**
	 * Determines whether or not a value for a particular provider is empty or not. While strings are easy to check, there
	 * are other types, such as string[], object[], date ranges, and such, which may pose more of an issue. This is required
	 * in combination with the nullable flag, to prevent invalid searches.
	 *
	 * @param {T} value
	 * @returns {boolean}
	 * @memberof FilterProviderBase
	 */
	isEmptyValue(value: T): boolean;
}

/*
	Hard-coded list of available filter types. If you want to add custom ones, 
	you must do that here. The simplest example is `SingleTextFilterProvider` - it is recommended to go through
	that one and fully understand it before creating a custom one.
*/
const types: { [type: string]: FilterProviderBase } = {};
types[FilterType.SingleText] = new SingleTextFilterProvider();
types[FilterType.MultipleText] = new MultipleValueFilterProvider();

types[FilterType.SingleInteger] = new SingleIntegerFilterProvider();
types[FilterType.SingleDecimal] = new SingleDecimalFilterProvider();
types[FilterType.SingleBoolean] = new SingleBooleanFilterProvider();

types[FilterType.RangedInteger] = new RangedIntegerFilterProvider();
types[FilterType.RangedDecimal] = new RangedDecimalFilterProvider();

types[FilterType.SingleAutoSuggest] = new SingleAutoSuggestValueFilterProvider();
types[FilterType.MultipleAutoSuggest] = new MultipleAutoSuggestValueFilterProvider();

types[FilterType.MultipleDropdown] = new MultipleDropdownFilterProvider();
types[FilterType.SingleDropdown] = new SingleDropdownFilterProvider();
types[FilterType.MultipleDateRangePreset] = new MultipleDateRangePresetProvider();
types[FilterType.SingleDateRangePreset] = new SingleDateRangePresetFilterProvider();
types[FilterType.SingleDate] = new SingleDateFilterProvider();

export const isGenericEmptyValue = (value: any) => typeof value === 'undefined' || value === null || value === '';

/**
 * Helper function that will return a valid filter edit component even if none is defined, just for added robustness.
 * @param type
 */
export const getFilterType = (type: FilterType): FilterProviderBase => {
	const requestedType = types[type];
	if (typeof requestedType === 'undefined') {
		return types[FilterType.SingleText];
	}

	return requestedType;
};

/**
 * Defines the target for QueryString generation. Client QueryString's are used to populate on the browser history.
 * Server QueryString's are sent to the server for query against data.
 *
 * @export
 * @enum {number}
 */
export enum QueryStringTarget {
	Client = 1,
	Server = 2,
}

/**
 * Generates the QueryString for all selected filters
 * @param selectedFilters
 * @param forClient Determines whether the query string being generated is going to be used by the client (for the URL)
 * or if it's going to be generated to be sent to the server. This allows us to hide certain values from the browser
 * while still making requests to the server with more details. Primarily used to hide date ranges into presets
 * such as "Today" or "MTD"
 */
export const getQueryStringForFilters = (
	selectedFilters: SelectedFilter[],
	target: QueryStringTarget = QueryStringTarget.Server,
): string => {
	if (!selectedFilters) {
		return '';
	}

	return selectedFilters.map((selectedFilter) => getQueryStringComponentForFilter(selectedFilter, target)).join('&');
};

/**
 * Generates the QueryString key/value string for a single selected filter
 * @param selectedFilter
 * @param forCLient Determines whether the query string is being generated for the client, or server.
 */
export const getQueryStringComponentForFilter = (
	selectedFilter: SelectedFilter,
	target: QueryStringTarget = QueryStringTarget.Server,
): string => {
	const name = encodeURIComponent(selectedFilter.name);
	const value = encodeURIComponent(getQueryStringValueForFilter(selectedFilter, target));

	if (!value) {
		return '';
	}

	return `${name}=${value}`;
};

/**
 * Generates the QueryString value string for a single selected filter
 */
export const getQueryStringValueForFilter = (
	selectedFilter: SelectedFilter,
	target: QueryStringTarget = QueryStringTarget.Server,
): string => getFilterType(selectedFilter.type).getQueryStringFromValue(selectedFilter.value, selectedFilter, target);

/**
 * Parses a Query String into an object, with some default values in case these are not provided in the QueryString.
 * @param queryString
 */
export const getParsedQueryString = (queryString: string) => {
	const params = new URLSearchParams(queryString);
	const result = {};
	params.forEach((value, key) => {
		result[key] = value;
	});

	return {
		page: 1,
		pageSize: 25,
		q: '',
		sortBy: '',
		sortDirection: '',
		savedFilterId: '',
		customSortingEnabled: result['customSortingEnabled'],
		...result,
	};
};

/**
 * Type definition for all search queries.
 */
export interface SearchQuery extends SearchQueryBase {
	filters: SelectedFilter[];
}

export interface SearchQueryBase {
	page: number;
	pageSize: number;
	sortBy: string | undefined;
	sortDirection: string | undefined;
	q: string;
	savedFilterId: string;
	customSortingEnabled?: boolean;
}

export interface SearchQueryNoFilters extends Partial<SearchQueryBase> {}

export interface SearchQueryFromSavedFilter {
	filters: SelectedFilter[];
}

const getValidatedFilters = (
	filterParams: Record<string, any>,
	entityDefinition: EntityDefinition,
): SelectedFilter[] => {
	// create a dictionary for type lookups.
	const definedFilters = entityDefinition.filters.reduce((acc, value) => {
		acc[value.name.toLowerCase()] = value;
		return acc;
	}, {});

	const initialFilters: SelectedFilter[] = [];
	const params = Object.keys(filterParams);

	for (const param of params) {
		const value = filterParams[param];
		const filter: Filter = definedFilters[param.toLowerCase()];

		if (!filter) {
			continue;
		}

		// use provider model to parse different filter types
		const type = getFilterType(filter.type);

		try {
			const parsedValue = type.getValueFromQueryString(value, filter);

			if (filter.nullable || !type.isEmptyValue(parsedValue)) {
				initialFilters.push({ ...filter, value: parsedValue });
			} else {
				console.warn(
					'Empty value found for non-nullable filter:',
					param,
					'using type provider:',
					type,
					'. Ignoring.',
				);
			}
		} catch (error) {
			console.warn('Unable to parse param:', param, 'with value:', value, 'using type provider:', type);
			console.error(error);
		}
	}

	const implicitFilters = entityDefinition.filters.filter((f) => f.implicit) as SelectedFilter[];
	const implicitFilterNames = implicitFilters.map((implicitFilter) => implicitFilter.name);

	// remove any filters from the saved filter that are being used by the implicit filter.
	return initialFilters.filter((f) => !implicitFilterNames.includes(f.name)).concat(implicitFilters);
};

/**
 * Parses the query string and using the filter provider model returns the basic search query along with other filters
 * required to prepopulate state, including filters
 * @param queryString
 * @param entityDefinition
 */
export const getSearchQueryFromQueryString = (queryString: string, entityDefinition: EntityDefinition): SearchQuery => {
	// parse the query string into a more usable object
	const parsedQueryString = getParsedQueryString(queryString);

	// extract expected properties and consider the remaining properties as filter query params
	const { page, pageSize, sortBy, sortDirection, q, savedFilterId, customSortingEnabled, ...filterParams } =
		parsedQueryString;

	const initialFilters = getValidatedFilters(filterParams, entityDefinition);

	return {
		page,
		pageSize,
		sortBy,
		sortDirection,
		q,
		savedFilterId,
		customSortingEnabled,
		filters: initialFilters,
	};
};

/**
 * Parses the query string and using the filter provider model returns the basic search query along with other filters
 * required to prepopulate state, including filters, but flattened so they can be passed in the body of a POST rather than
 * in the QueryString of a GET request.
 * @param queryString
 * @param entityDefinition
 */
export const getSearchQueryFromQueryStringWithFlatFilters = (
	queryString: string,
	entityDefinition: EntityDefinition,
	target: QueryStringTarget = QueryStringTarget.Server,
	ignoreKeys = ['page', 'pageSize'],
): SearchQueryNoFilters => {
	// parse the query string into a more usable object
	const parsedQueryString = getParsedQueryString(queryString);

	// extract expected properties and consider the remaining properties as filter query params
	const { page, pageSize, sortBy, sortDirection, q, savedFilterId, customSortingEnabled, ...filterParams } =
		parsedQueryString;

	const initialFilters = getValidatedFilters(filterParams, entityDefinition);

	const result = {
		page,
		pageSize,
		sortBy,
		sortDirection,
		q,
		savedFilterId,
		customSortingEnabled,
	};

	const filtersObject = initialFilters.reduce((acc, value) => {
		acc[value.name] = getQueryStringValueForFilter(value, target);
		return acc;
	}, {});

	const resultsQuery = {
		...result,
		...filtersObject,
	};

	const filteredResultsQuery = Object.keys(resultsQuery)
		.filter((key) => {
			const value = resultsQuery[key];

			if (Array.isArray(value) && value.length > 0) {
				return true;
			}

			if (!Array.isArray(value) && value) {
				return true;
			}

			return false;
		})
		.reduce((acc, value) => {
			if (!ignoreKeys.includes(value)) {
				acc[value] = resultsQuery[value];
			}

			return acc;
		}, {});

	return filteredResultsQuery;
};

/**
 * Generates a search query based on a saved filter definition
 * @param savedFilter
 * @param entityDefinition
 */
export const getSearchQueryFromSavedFilter = (
	savedFilter: SavedFilter,
	entityDefinition: EntityDefinition,
): SearchQueryFromSavedFilter => {
	const filterParams = savedFilter.query;
	const initialFilters = getValidatedFilters(filterParams, entityDefinition);

	return {
		filters: initialFilters,
	};
};

/**
 * Converts a `SearchQuery` instance into a query string, which will be used to execute searches on
 * the server, as well as keep browser history for different actions on the listing page.
 * @param searchQuery
 * @param forClient
 */
export const getQueryStringFromSearchQuery = (
	searchQuery: Partial<SearchQuery>,
	target: QueryStringTarget = QueryStringTarget.Server,
): string => {
	const values: string[] = [];

	if (!isGenericEmptyValue(searchQuery.page) && searchQuery.page !== 1) {
		values.push('page=' + searchQuery.page);
	}

	if (searchQuery.q) {
		values.push('q=' + encodeURIComponent(searchQuery.q));
	}

	// prioritize a saved filter
	if (searchQuery.savedFilterId && target === QueryStringTarget.Client) {
		values.push('savedFilterId=' + encodeURIComponent(searchQuery.savedFilterId));
	} else if (searchQuery.filters && searchQuery.filters.length > 0) {
		const filters =
			target === QueryStringTarget.Client ? searchQuery.filters.filter((f) => !f.implicit) : searchQuery.filters;

		values.push(getQueryStringForFilters(filters, target));
	}

	if (searchQuery.sortBy) {
		values.push('sortBy=' + encodeURIComponent(searchQuery.sortBy));
	}

	if (searchQuery.sortDirection) {
		values.push('sortDirection=' + encodeURIComponent(searchQuery.sortDirection));
	}

	if (searchQuery.customSortingEnabled) {
		values.push('customSortingEnabled=' + encodeURIComponent(searchQuery.customSortingEnabled));
	}

	if (values.length === 0) {
		return '';
	}

	return '?' + values.join('&');
};
